import { Currency } from '../general/general.types';

// #region restaurant enum
export enum DeliveryType {
  Delivery = 0,
  Collection = 1, // This is called 'Pickup' on the server.
}

export enum PickupLocationType {
  TakeOut = 0,
  TableService = 1,
  DineIn = 2,
}

export enum TableServiceCategory {
  Generic = 0,
  Villa = 1,
  House = 2,
  Room = 3,
  Area = 4,
  Table = 5,
  ParkingBay = 6,
  Gate = 7,
  DriveThrough = 8,
}
// #endregion

// #region restaurant common types
export interface Opening {
  IsOpen: boolean;
  ClosingTimeStamp?: Date;
  OpenTimeHour: number;
  OpenTimeMinute: number;
  OpenTimeMessage?: string;
  SecondsUntilRestaurantOpens?: number;
  SecondsUntilRestaurantCloses?: number;
}

export interface CuisineType {
  CuisineTypeId: number;
  Name: string;
}
// #endregion

// #region restaurant summary types
export interface RestaurantSummaryCommon extends Opening {
  AcceptPreOrders: boolean;
  CuisineTypes: CuisineType[];
  Currency: Currency;
  DistanceKm: number;
  GeneralRating?: number;
  GeneralRatingCount: number;
  IanaTimeZone: string;
  IsoCurrency: string;
  IsOpen: boolean;
  MenuId: number;
  MenuUrl: string;
  MenuVersionNumber: number;
  PhysicalRestaurantAddress: string;
  PhysicalRestaurantId: number;
  PhysicalRestaurantName: string;
  StripePublicKey: string;
  TableServiceCatagory: TableServiceCategory;
  TimeZoneInfoId: string;
  UserRating?: any;
  UserRatingCount: number;
  VersionGuid: string;
  VirtualRestaurantId: number;
}

export interface CollectionRestaurantSummary extends RestaurantSummaryCommon {
  HasConcessionStore?: boolean;
  IncludePhysicalRestaurantIds: string;
  PhysicalRestaurantId: number;
  PickupLocationTypes: PickupLocationType[];
  SkipToThisPhysicalRestaurant: boolean;
  VirtualRestaurantName: string;
}

export interface DeliveryRestaurantSummary extends RestaurantSummaryCommon {
  DeliveryNote: string;
  HasConcessionStore?: boolean;
  IsFreeDelivery: boolean;
  Name: string;
  SkipToThisVirtualRestaurant: boolean;
  UserOutsideDeliveryZone: boolean;
}

export interface GetCollectionSummariesQueryParams {
  count: number;
  includePhysicalRestaurantIds?: string;
  latitude: number;
  longitude: number;
  skip: number;
}

export interface GetDeliverySummariesQueryParams {
  count: number;
  deviceId: string;
  latitude: number;
  longitude: number;
  skip: number;
}

export interface GetDeliveryDetailQueryParams {
  deliveryLocationId: number;
  deviceId: string;
}
// #endregion

// #region restaurant detail types
export interface RestaurantDetail extends CardFees, CashFees, Opening {
  AcceptCardOrders: boolean;
  AcceptCashOrders: boolean;
  AcceptPreOrders: boolean;
  AcceptRestaurantVoucherOrders: boolean;
  AllowChefNotes: boolean;
  AppImageUrl: string;
  ChefNote?: string;
  Currency: Currency;
  DeliveryFeeTaxAmount: number;
  DisplayPhoneNumber: string;
  DisplayTax: boolean;
  IsoCurrency: string;
  MenuId: number;
  MenuMessage?: string;
  MenuUrl: string;
  MenuVersionNumber: number;
  MinimumOrder: number;
  NutritionInfoUrl: string;
  PhoneNumber: string;
  PhysicalRestaurantAddress: string;
  PhysicalRestaurantId: number;
  PreOrderOptions?: {
    PreOrderTimes: PreOrderTime[];
    RequireExplicitSelect: boolean;
  };
  ProcessingFeeTaxRate: number;
  RequireCustomerName: boolean;
  RestaurantName: string;
  ServiceChargeConfiguration: ServiceChargeConfig | null;
  TaxType: number;
  TimeZoneInfoId: string;
  TipsConfig: TipsConfig;
  TipsEnabled: boolean;
  VirtualRestaurantId: number;
}

export interface CardFees {
  PercentFeeCardOrdersChargedToCustomerIncludingVat: number;
  FixedFeeCardOrdersChargedToCustomerIncludingVat: number;
  LocationServicePercentFeeCardOrdersChargedToCustomerIncludingVat: number;
  LocationServiceFixedFeeCardOrdersChargedToCustomerIncludingVat: number;
}

export interface CashFees {
  PercentFeeCashOrdersChargedToCustomerIncludingVat: number;
  FixedFeeCashOrdersChargedToCustomerIncludingVat: number;
  LocationServicePercentFeeCashOrdersChargedToCustomerIncludingVat: number;
  LocationServiceFixedFeeCashOrdersChargedToCustomerIncludingVat: number;
}

export interface PreOrderTime {
  Key: string;
  Value: string;
  IsFull?: boolean;
}

export interface ServiceChargeConfig {
  AmountExcludingProcessingFee: number | null;
  DisplayAsOneWithProcessingFee: boolean;
  DisplayPercentage: number | null;
  IsEnabled: boolean;
  IsOptional: boolean;
}

export interface TipsConfig {
  AllowCustomTip: boolean;
  AllowEmojis: boolean;
  AllowRoundUp: boolean;
  Items: TipRate[];
}

export interface TipRate {
  Percentage: number;
  IsDefault: boolean;
}

export interface CollectionRestaurantDetail extends RestaurantDetail {
  AllowGuestCheckout: boolean;
  AllowPreOrdersAndTableService: boolean;
  DeliveryType: DeliveryType.Collection;
  PickupLocationOptions: PickupLocationOption[];
  PickupLocationTypes: PickupLocationType[];
  IsTableLockEnabled: boolean;
  TableServiceCatagory: TableServiceCategory;
  LocationAreas: CollectionLocationArea[];
}

export interface PickupLocationOption {
  PickupLocationOptionId: number;
  Value: string;
  DisplayOrder: number;
}

export interface DeliveryRestaurantDetail extends RestaurantDetail {
  UserOutsideDeliveryZone: boolean;
  DeliveryType: DeliveryType.Delivery;
  DeliveryFee: number;
}
// #endregion

// #region concession restaurant types
export interface ConcessionStore {
  ConcessionStoreId: number;
  Description: string;
  ImageName: string;
  ImageUrl: string;
  Name: string;
}
// #endregion

// #region collection location area types
export interface CollectionLocationArea {
  LocationAreaId: number;
  LocationAreaName: string;
  Locations: Location[];
  IsDeleted: boolean;
  StoreId: number;
}

export interface Location {
  DisplayOrder: number;
  ExternalLocationId: string;
  LocationId: number;
  LocationName: string;
  IsDeleted: boolean;
}

export interface GetCollectionLocationAreasQueryParams {
  storeId: number;
}
// #endregion

// #region loyalty types
export interface Campaign {
  CampaignId: number;
  Description: string;
  RequiredConditions?: LoyaltyRequiredCondition[];
  Rewards?: Reward[];
  Type: string;
  Voucher?: LoyaltyVoucher;
}

export interface LoyaltyRequiredCondition {
  CurrentValue: string;
  Description: string;
  RequiredValue: string;
  Type: string;
}

export interface LoyaltyVoucher {
  AutoApplies: boolean;
  Code: string;
  ExpireDate: string;
  StartDate: string;
}

export interface Reward {
  Type: 'PercentageDiscount' | 'LumpDiscount';
  Value: string;
  Description: string;
  Modifiers: RewardModifier[];
}

export interface RewardModifier {
  type: string;
  value: string;
}

export interface GetCampaignProgressQueryParams {
  appId: string;
  storeIDs: number;
}
// #endregion
